// Eigenbau: sichere Mermaid-Vorschau mit Limits, Debounce und dynamischem Import
import { useEffect, useId, useRef, useState } from "react";
import type { MermaidConfig } from "mermaid";

const MAX_TEXT = 20_000;
const MAX_EDGES = 200;
const DEBOUNCE_MS = 500;

export function sichereMermaidKonfiguration(dunkel: boolean): MermaidConfig {
  return {
    startOnLoad: false,
    securityLevel: "strict",
    htmlLabels: false,
    maxTextSize: MAX_TEXT,
    maxEdges: MAX_EDGES,
    theme: dunkel ? "dark" : "neutral",
    fontFamily: "IBM Plex Sans, ui-sans-serif, sans-serif",
    suppressErrorRendering: true,
    secure: [
      "securityLevel",
      "maxTextSize",
      "maxEdges",
      "htmlLabels",
      "dompurifyConfig",
      "themeCSS",
      "fontFamily",
      "altFontFamily",
      "themeVariables",
      "legacyMathML",
      "forceLegacyMathML",
    ],
  };
}

type Props = {
  quelle: string;
  className?: string;
};

export function MermaidVorschau({ quelle, className }: Props) {
  const hostId = useId().replace(/:/g, "");
  const [svg, setzeSvg] = useState<string | null>(null);
  const [fehler, setzeFehler] = useState<string | null>(null);
  const [dunkel, setzeDunkel] = useState(() =>
    typeof document === "undefined"
      ? false
      : document.documentElement.classList.contains("dark"),
  );
  const sequenz = useRef(0);

  useEffect(() => {
    const wurzel = document.documentElement;
    const beobachten = new MutationObserver(() => {
      setzeDunkel(wurzel.classList.contains("dark"));
    });
    beobachten.observe(wurzel, { attributes: true, attributeFilter: ["class"] });
    return () => beobachten.disconnect();
  }, []);

  useEffect(() => {
    const meine = ++sequenz.current;
    setzeFehler(null);

    if (quelle.length > MAX_TEXT) {
      setzeSvg(null);
      setzeFehler(`Diagramm zu groß (max. ${MAX_TEXT} Zeichen).`);
      return;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const mermaid = (await import("mermaid")).default;
          mermaid.initialize(sichereMermaidKonfiguration(dunkel));
          const { svg: gerendert } = await mermaid.render(
            `mermaid-${hostId}-${meine}`,
            quelle,
          );
          if (sequenz.current === meine) {
            setzeSvg(gerendert);
            setzeFehler(null);
          }
        } catch (err) {
          if (sequenz.current === meine) {
            setzeSvg(null);
            setzeFehler(
              err instanceof Error ? err.message : "Mermaid-Syntaxfehler",
            );
          }
        }
      })();
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [quelle, hostId, dunkel]);

  if (fehler) {
    return (
      <div role="alert" className={className} aria-live="polite">
        <p className="font-mono text-[11.5px] text-warn">{fehler}</p>
        <pre className="mt-3 overflow-auto border-l-2 border-warn bg-code p-4 text-xs">
          {quelle}
        </pre>
      </div>
    );
  }

  if (!svg) {
    return (
      <p className="font-mono text-[11.5px] text-tinte4" role="status">
        Diagramm wird gerendert …
      </p>
    );
  }

  return (
    <div
      className={["seiten-diagramm-svg", className].filter(Boolean).join(" ")}
      role="img"
      aria-label="Mermaid-Diagramm"
      // Mermaid liefert SVG-Markup; securityLevel strict + keine Klickaktionen.
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}
